import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Phone } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, apiErrorMessage } from "@/lib/api";
import { rupees, tradeLabel, urgencyLabel } from "@/lib/format";
import { isUuid } from "@/lib/ids";
import { telHref } from "@/lib/phone";
import { StarRating } from "@/components/StarRating";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const POLL_MS = 15000;

/** Citizen task detail (a SERVICE REQUEST, not a classified job): compare worker offers, choose
 * one, mark the work complete, then rate. Gating mirrors mobile core `TaskDetail.tsx` exactly. */
export default function TaskDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  usePageTitle(t("tasks.taskDetail")); // R4-7: per-page tab title
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | forbidden | error
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState("");

  const load = useCallback(() => {
    // A non-UUID id can never be a request (the endpoint would 422): show "not found" directly.
    if (!isUuid(id)) {
      setState("missing");
      return Promise.resolve();
    }
    return api
      .get(`/services/requests/${id}`)
      .then((r) => {
        setItem(r.data);
        setState("ready");
      })
      .catch((e) => {
        const status = e?.response?.status;
        // RF-23: someone else's request answers 403 - say so, rather than "Something went wrong".
        setState(status === 404 ? "missing" : status === 403 ? "forbidden" : "error");
      });
  }, [id]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  // Offers arrive while the request is open — keep the list fresh.
  useEffect(() => {
    if (item?.status !== "open") return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [item?.status, load]);

  // RF-23: `busy` is state, so a fast double-click ran `act` twice before it could disable the
  // button; the second "Mark complete" then failed on an already-completed request. A ref blocks
  // the second call synchronously, and a 409 (already moved on) just refreshes instead of erroring.
  const inFlight = useRef(false);
  const act = async (path, body) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      await api.post(path, body);
      setConfirmComplete(false);
      setConfirmCancel(false);
      await load();
    } catch (e) {
      if (e?.response?.status === 409) {
        setConfirmComplete(false);
        setConfirmCancel(false);
        await load();
      } else {
        setError(apiErrorMessage(e, t("errors.generic")));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (state === "forbidden")
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="task-forbidden">
        <p className="text-lg">{t("tasks.notYours")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/activity")}>
          {t("common.back")}
        </Button>
      </div>
    );
  if (state === "missing")
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="task-not-found">
        <p className="text-lg">{t("tasks.notFound")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/activity")}>
          {t("common.back")}
        </Button>
      </div>
    );
  if (state === "error")
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="task-error">
        <p className="text-lg">{t("errors.generic")}</p>
        <Button className="mt-6 rounded-full" onClick={() => { setState("loading"); load(); }} data-testid="task-retry-btn">
          {t("common.retry")}
        </Button>
      </div>
    );
  if (state === "loading" || !item)
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 py-12" data-testid="task-loading" aria-busy="true">
        <div className="h-20 animate-pulse rounded-2xl bg-muted" />
        <div className="h-36 animate-pulse rounded-2xl bg-muted" />
      </div>
    );

  // `viewer_is_customer` is a UI hint (W3): a worker who can read an open same-trade request must
  // not be offered Cancel/Choose. The server already 404s every non-requester mutation. Absent
  // (an older server) means "assume the requester" — the old behaviour.
  const isCustomer = item.viewer_is_customer !== false;
  const quotes = (item.quotes ?? []).filter((q) => q.status !== "withdrawn");
  const priced = quotes.filter((q) => q.price != null);
  const lowest = priced.length ? Math.min(...priced.map((q) => q.price.minor_units)) : null;
  const chosen = quotes.find((q) => q.id === item.assigned_quote_id);
  // R4-10: the trade slug is stored lowercase ("plumber"); show it as a label.
  const trade = tradeLabel(lang, item);
  // R4-10: "Compare prices" describes how an OPEN request collects offers - on a finished one it
  // is just noise.
  const finished = ["completed", "cancelled", "expired", "refunded"].includes(item.status);
  const route =
    item.pickup && item.dropoff ? `${item.pickup} → ${item.dropoff}` : item.pickup || item.dropoff || item.area || "";
  const meta = [trade, route, urgencyLabel(t, lang, item.urgency, item.created_at)].filter(Boolean).join(" · ");

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8" data-testid="task-detail-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">{t("tasks.taskDetail")}</h1>

      <div className="mt-6 space-y-6">
        <div className="space-y-2 rounded-2xl border border-border p-5">
          <div className="flex flex-wrap gap-2">
            <Badge data-testid="task-status-badge">{t(`tasks.statusLabels.${item.status}`)}</Badge>
            {!finished && (
              <Badge variant="outline" data-testid="task-mode-badge">
                {t(item.pricing_mode === "fcfs" ? "tasks.modeFcfs" : "tasks.modeBid")}
              </Badge>
            )}
          </div>
          <p className="font-semibold" data-testid="task-description">{item.description}</p>
          {meta && <p className="text-sm text-muted-foreground">{meta}</p>}
          {item.workers_notified != null && (
            <p className="text-xs text-muted-foreground" data-testid="task-alerted">
              {t("tasks.workersAlerted", { count: item.workers_notified })}
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-destructive" data-testid="task-action-error">
            {error}
          </p>
        )}

        <div className="space-y-3">
          <h2 className="font-heading text-lg font-semibold">{t("tasks.bids")}</h2>
          {quotes.length === 0 ? (
            <div className="rounded-2xl border border-border p-5 text-sm" data-testid="task-no-bids">
              <p className="font-semibold">{t("tasks.noBids")}</p>
              <p className="text-muted-foreground">{t("tasks.noBidsHint")}</p>
            </div>
          ) : (
            quotes.map((q) => (
              <div key={q.id} className="space-y-2 rounded-2xl border border-border p-5" data-testid={`task-quote-${q.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold">{q.worker_name}</p>
                  {q.price ? (
                    <p className="font-extrabold" data-testid={`task-quote-price-${q.id}`}>{rupees({ minor_units: q.price.minor_units })}</p>
                  ) : (
                    <Badge variant="outline">{t("tasks.noPrice")}</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {lowest !== null && q.price?.minor_units === lowest && <Badge variant="outline">{t("tasks.lowest")}</Badge>}
                  {q.id === item.assigned_quote_id && <Badge>{t("tasks.chosen")}</Badge>}
                </div>
                {q.message && <p className="text-sm text-muted-foreground">{q.message}</p>}
                {isCustomer && item.status === "open" && (
                  <Button
                    className="rounded-full"
                    disabled={busy}
                    onClick={() => act(`/services/quotes/${q.id}/accept`)}
                    data-testid={`task-choose-${q.id}`}
                  >
                    {t("tasks.choose")}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {isCustomer && item.status === "open" &&
          (confirmCancel ? (
            <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="task-cancel-card">
              <p>{t("tasks.cancelConfirm")}</p>
              <div className="flex gap-2">
                <Button className="rounded-full" disabled={busy} onClick={() => act(`/services/requests/${id}/cancel`)} data-testid="task-cancel-confirm-btn">
                  {t("tasks.cancelTask")}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmCancel(false)} data-testid="task-cancel-keep-btn">
                  {t("common.back")}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmCancel(true)} data-testid="task-cancel-btn">
              {t("tasks.cancelTask")}
            </Button>
          ))}

        {isCustomer && chosen && item.status === "assigned" && (
          <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="task-assigned-card">
            <p className="font-heading text-lg font-semibold">{chosen.worker_name}</p>
            {chosen.price && <p className="font-bold">{rupees({ minor_units: chosen.price.minor_units })}</p>}
            {telHref(chosen.worker_phone) && (
              <a
                href={telHref(chosen.worker_phone)}
                className="inline-flex min-h-[40px] items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 text-sm font-semibold text-primary-foreground"
                data-testid="task-call-worker-btn"
              >
                <Phone size={14} /> {t("tasks.callWorker")}
              </a>
            )}
            {confirmComplete ? (
              <div className="space-y-3">
                <p>{t("tasks.completeConfirm")}</p>
                <div className="flex gap-2">
                  <Button className="rounded-full" disabled={busy} onClick={() => act(`/services/requests/${id}/complete`)} data-testid="task-complete-confirm-btn">
                    {t("tasks.markComplete")}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmComplete(false)} data-testid="task-complete-back-btn">
                    {t("common.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmComplete(true)} data-testid="task-complete-btn">
                {t("tasks.markComplete")}
              </Button>
            )}
          </div>
        )}

        {isCustomer && item.status === "completed" &&
          (item.rated ? (
            <p className="rounded-2xl border border-border p-5 text-sm" data-testid="task-rated">
              {t("tasks.rated")}
            </p>
          ) : (
            <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="task-rate-card">
              <p className="font-semibold">{t("tasks.rateTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("tasks.rateHint")}</p>
              <StarRating value={stars} onChange={setStars} size={26} testId="task-rate-stars" />
              <Input value={review} onChange={(e) => setReview(e.target.value)} data-testid="task-review-input" />
              <Button
                className="rounded-full"
                disabled={stars === 0 || busy}
                onClick={() => act(`/services/requests/${id}/rate-worker`, { rating: stars, body: review })}
                data-testid="task-rate-submit-btn"
              >
                {t("tasks.rateSubmit")}
              </Button>
            </div>
          ))}
      </div>
    </div>
  );
}
